import { FormAggregatedResult } from "@/components/form-result/aggregated-result/FormAggregatedResult.tsx";
import { FormAggregatedResultFilterOptions } from "@/components/form-result/aggregated-result/components/types.ts";
import { PerformanceReviewSummaryChart } from "@/pages/talent/performance-reviews/view/components/PerformanceReviewSummaryChart.tsx";
import { PerformanceReviewQuestionResultsModal } from "@/pages/talent/performance-reviews/view/widgets/PerformanceReviewQuestionResultsModal.tsx";
import {
  usePerformanceReviewReviewerTypeAggregatedResults,
  usePerformanceReviewStats,
} from "@/queries/performance-reviews/use-performance-reviews.query.ts";
import { ApiFormViewElement } from "@/types/forms/form-view.types.ts";
import {
  ApiPerformanceReview,
  ApiPerformanceReviewReviewerStatsBase,
  getApiPerformanceReviewStatusTitle,
  PerformanceReviewReviewerTypeItems,
} from "@/types/performance-review.types.ts";
import {
  Box,
  MetricTile,
  Select,
  SelectItem,
  SelectValueType,
  Skeleton,
  Stack,
  useModals,
} from "@skailer-com/uikit";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { twMerge } from "tailwind-merge";

export const PerformanceReviewSummary = ({ review }: { review: ApiPerformanceReview }) => {
  const { t } = useTranslation();
  const { openModal } = useModals();

  const { data: reviewStats, isLoading: isReviewStatsLoading } = usePerformanceReviewStats(
    review.id
  );

  const [statsCurrentReviewerTypeId, setStatsCurrentReviewerTypeId] =
    useState<SelectValueType>("all");

  const currentReviewStats = useMemo(() => {
    if (!reviewStats) return;

    if (statsCurrentReviewerTypeId !== "all") {
      const stats = reviewStats.stat_by_reviewer_types.find(
        (stats) => stats.reviewer_type_id === statsCurrentReviewerTypeId
      );
      if (stats) {
        return stats as ApiPerformanceReviewReviewerStatsBase;
      } else
        return {
          total_count: 0,
          completed_count: 0,
          added_count: 0,
          invited_count: 0,
          in_progress_count: 0,
          completed_by_periods: [],
        } as ApiPerformanceReviewReviewerStatsBase;
    } else {
      return reviewStats as ApiPerformanceReviewReviewerStatsBase;
    }
  }, [reviewStats, statsCurrentReviewerTypeId]);

  const statsReviewerTypeOptions = useMemo(() => {
    const options = review.reviewer_types.map((x) => ({
      id: x.id,
      title:
        PerformanceReviewReviewerTypeItems().find((item) => item.id == x.type)?.title ??
        x.title ??
        "",
    })) as SelectItem[];

    return [{ id: "all", title: t("performance_review:overall") }, ...options];
  }, [review]);

  const reviewerTypeOptions = useMemo(() => {
    return review.reviewer_types.map((x) => ({
      id: x.id,
      title:
        PerformanceReviewReviewerTypeItems().find((item) => item.id == x.type)?.title ??
        x.title ??
        "",
    })) as SelectItem[];
  }, [review]);

  const [resultsCurrentReviewerTypeId, setResultsCurrentReviewerTypeId] = useState<
    number | undefined
  >(review.reviewer_types[0]?.id);

  const { data: reviewerTypeResults, isLoading: isReviewerTypeFormLoading } =
    usePerformanceReviewReviewerTypeAggregatedResults(
      review.id,
      resultsCurrentReviewerTypeId,
      !!resultsCurrentReviewerTypeId
    );

  const handleShowDetailModalCallback = (
    element: ApiFormViewElement,
    filterOptions?: FormAggregatedResultFilterOptions
  ) => {
    if (!resultsCurrentReviewerTypeId) return;

    const resultAggregation = reviewerTypeResults?.results.elements.find(
      (el) => el.form_element_id === element.id
    );

    openModal(PerformanceReviewQuestionResultsModal, {
      performanceReviewId: review.id,
      reviewTypeId: resultsCurrentReviewerTypeId,
      referenceId: element.reference_id,
      element: element,
      elementIds: [element.id],
      answerScaleOptionIds: filterOptions?.answer_scale_option_ids,
      answerSelectOptionIds: filterOptions?.answer_select_option_ids,
      selectableOptionIds: filterOptions?.selectable_option_ids,
      resultAggregation: resultAggregation,
      managers: filterOptions?.managers,
      departments: filterOptions?.departments,
    });
  };

  return (
    <Box>
      <Box className={twMerge("grid md:grid-cols-4 print:grid-cols-4 gap-sm py-lg")}>
        <Select
          label={""}
          variant={"default"}
          options={statsReviewerTypeOptions}
          emptyTitle={t("common:select")}
          value={statsCurrentReviewerTypeId}
          onChange={(value) => {
            setStatsCurrentReviewerTypeId(value);
          }}
        />
        <div className="col-span-4" />
        {isReviewStatsLoading && (
          <>
            <Skeleton className={"w-full h-15"} />
            <Skeleton className={"w-full h-15"} />
            <Skeleton className={"w-full h-15"} />
            <Skeleton className={"w-full h-15"} />
          </>
        )}
      </Box>
      {currentReviewStats && (
        <>
          <Box className={"py-(--gap-lg)"}>
            <Stack gap={"md"} direction={"horizontal"}>
              <MetricTile
                className={"flex-1"}
                title={t("common:status")}
                value={getApiPerformanceReviewStatusTitle(review.status)}
              />
              <MetricTile
                className={"flex-1"}
                title={t("common:total")}
                value={currentReviewStats.total_count}
              />
              <MetricTile
                className={"flex-1"}
                title={t("surveys:participant_status_completed")}
                value={currentReviewStats.completed_count}
              />
              <MetricTile
                className={"flex-1"}
                title={t("surveys:participants_waiting")}
                value={currentReviewStats.invited_count + currentReviewStats.in_progress_count}
              />

              <MetricTile
                className={"flex-1"}
                title={t("performance_review:unsolicited")}
                value={review.reviewer_stats.unsolicited_count}
              />

              <MetricTile
                className={"flex-1"}
                title={t("common:response_rate")}
                value={
                  currentReviewStats.total_count > 0
                    ? (
                        (currentReviewStats.completed_count / currentReviewStats.total_count) *
                        100
                      ).toFixed() + "%"
                    : "-"
                }
              />
            </Stack>
          </Box>
        </>
      )}
      <Box className={twMerge("pb-8")}>
        {currentReviewStats && currentReviewStats.completed_by_periods.length > 1 && (
          <PerformanceReviewSummaryChart reviewerStats={currentReviewStats} />
        )}
      </Box>

      <Box className={twMerge("pt-10")}>
        <Stack className={"container-md mx-auto"}>
          {reviewerTypeOptions.length > 0 && (
            <Box className="grid md:grid-cols-3">
              <Box className="col-start-3">
                <Select
                  label={""}
                  variant={"default"}
                  options={reviewerTypeOptions}
                  emptyTitle={t("common:select")}
                  value={resultsCurrentReviewerTypeId}
                  onChange={(value) => {
                    setResultsCurrentReviewerTypeId(Number(value));
                  }}
                />
              </Box>
            </Box>
          )}

          {isReviewerTypeFormLoading && (
            <Box className={twMerge("pt-10")}>
              <Skeleton className={"w-full h-50"} />
            </Box>
          )}

          {reviewerTypeResults && (
            <Box className={"-ml-14 relative"}>
              <FormAggregatedResult
                form={reviewerTypeResults.form}
                results={reviewerTypeResults.results}
                showDetailModalCallback={handleShowDetailModalCallback}
              />
            </Box>
          )}
        </Stack>
      </Box>
    </Box>
  );
};
